import { useEffect, useState } from "react";
import { CurrentBrand } from "modules/Types";
import { useHistory, useLocation } from "react-router-dom";
import ConfirmChangeSubscriptionModal from "../componentsOrganization/ConfirmChangeSubscriptionModal";
import { SHARED_TOAST_OPTIONS, SHARED_TOAST_OPTIONS_ERROR, toastError } from "modules/Toast";
import { toast } from "react-toastify";
import AddBrandToOrganizationModal from "../componentsOrganization/AddBrandToOrganizationModal/AddBrandToOrganizationModal";
import OrganizationTabSection from "../OrganizationTabSection";
import { GridColDef } from "@mui/x-data-grid";
import { Body2, Caption } from "components/shareable/Typography";
import OrganizationViewDefaultGrid from "../componentsOrganization/OrganizationViewDefaultGrid";
import UpgradeToProChip from "components/shareable/UpgradeToProChip";
import millify from "millify";
import BrandActionMenu from "../componentsOrganization/BrandActionMenu";
import useOrganization from 'Hooks/useOrganization';
import { isOrganizationAdminOrOwner, pluralize, sortIds } from "modules/Utils";
import Colors from "modules/Colors";
import { addBrandsToOrganization_api } from "api/organizationApi";
import useCurrentUser from 'Hooks/useCurrentUser';
import SearchSelector from "components/ProfileDropdown/AdminSearchSelectorModal";
import useCurrentBrand, { BrandExistsState } from 'Hooks/useCurrentBrand';
import DeleteBrandConfirmationModal from "../componentsOrganization/DeleteBrandConfirmationModal";
import ButtonSymphony from "elements/ButtonSymphony";
import { PlanType, SortDirection } from "modules/Const";
import CustomAvatar from "components/ProfileDropdown/CustomAvatar";

const PAGE_SIZE = 8;

const OrganizationTabBrands = () => {
    const history = useHistory();
    const location = useLocation();
    const {
        organization,
        organizationBrandIds,
        getOrganizationBrands,
        updateMemberStatusInOrganization,
        setSelectedBrandId,
        getSelectedBrand,
        getOrganizationId
    } = useOrganization();
    const { isSymphonyAdmin } = useCurrentUser();
    const {
        loadedBrandsDict,
        isLoadingBrands,
        setCurrentBrand,
        getBrands,
        removeBrand,
        getFanbaseSize
    } = useCurrentBrand() as BrandExistsState

    const selectedBrand = getSelectedBrand();

    const isAdminOrOwner = isSymphonyAdmin || isOrganizationAdminOrOwner(organization)

    const [searchValue, setSearchValue] = useState<string>('')
    let filteredOrganizationBrandIds = organizationBrandIds.filter(brandId => loadedBrandsDict[brandId]?.name.toLowerCase().includes(searchValue.toLowerCase()))
    filteredOrganizationBrandIds = sortIds(loadedBrandsDict, filteredOrganizationBrandIds, 'name', SortDirection.ASC)

    const [slugParam, setSlugParam] = useState<string | null>(new URLSearchParams(location.search).get('slug'));
    const [showAddArtistModal, setShowAddArtistModal] = useState(false);

    // Used for Admins - Invite from Admin Search popup
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openAdminSelector, setOpenAdminSelector] = useState<boolean>(false)
    const handleCloseAdminSelectorDropdown = () => {
        setAnchorEl(null);
        setOpenAdminSelector(false)
    }

    // upgrade modal
    const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false)
    const [isOpenDeleteConfirmationModal, setIsOpenDeleteConfirmationModal] = useState<boolean>(false);
    const [inProgressUpdatingBrandSlug, setInProgressUpdatingBrandSlug] = useState<{
        slug: string;
        status: 'upgrade' | 'downgrade'
    } | null>(null)

    // Selected Artist State
    const [page, setPage] = useState<number>(0);

    useEffect(() => {
        // when this slug is updated, we should open the modal
        if (inProgressUpdatingBrandSlug) {
            const selectedBrandId = filteredOrganizationBrandIds.find(brandId => loadedBrandsDict[brandId]?.slug === inProgressUpdatingBrandSlug.slug)
            if (selectedBrandId) {
                setSelectedBrandId(selectedBrandId)
            }
            setShowUpgradeModal(true)
        }
    }, [inProgressUpdatingBrandSlug])

    // useEffect(() => {
    //     if (!showUpgradeModal) {
    //         setInProgressUpdatingBrandSlug(null)
    //     }
    // }, [showUpgradeModal])

    // useEffect(() => {
    //     if (slugParam && filteredOrganizationBrandIds.length) {
    //         beginUpgradeToPro(slugParam)
    //         setSlugParam(null)
    //     }
    // }, [slugParam, filteredOrganizationBrandIds])

    /**
     * For Admins only:
     * Handles importing multiple brands into an organization at once.
     * Takes the imported brands array, extracts valid slugs, and adds them to the organization.
     * Cleans up state and closes modal on success.
     */
    const adminViewImportBrands = async (brands: CurrentBrand[]) => {
        if (brands.length) {
            const brandIds = brands.map(brand => brand.id).filter((id): id is number => id !== null)
            try {
                toast.info("Adding profiles... This may take a few seconds.", SHARED_TOAST_OPTIONS)
                const addedBrands = await addBrandsToOrganization_api(getOrganizationId(), brandIds, true)
                if (addedBrands) {
                    handleCloseAdminSelectorDropdown()
                    toast.success("Profiles successfully added.", SHARED_TOAST_OPTIONS)

                }
            } catch (error) {
                console.error(error)
                toastError("There was an error adding profiles.")
            }
        }
    }

    /**
     * This lets you upgrade a brand to Pro within the organization - the backend handles
     * the billing and updates the status of the brand to Pro.
     * @param slug slug of brand that represents the upgrade
     */
    const beginUpgradeToPro = (brandId?: number) => {
        const brandIdLocal = brandId ?? selectedBrand!.id
        setSelectedBrandId(brandIdLocal)
        const brand = loadedBrandsDict[brandIdLocal]; // NOTE: need this because state change is async
        const {
            slug,
        } = brand

        setInProgressUpdatingBrandSlug({
            slug,
            status: 'upgrade'
        })
    }

    const beginDowngradeToFree = () => {
        const {
            id,
            slug,
        } = selectedBrand!

        setSelectedBrandId(id)
        setInProgressUpdatingBrandSlug({
            slug,
            status: 'downgrade'
        })
    }

    const handleProceedWithSubscriptionAction = async () => {
        // set the selected brand to isLoading state
        // const updatedBrands = organizationBrandIds.map(brandId => loadedBrandsDict[brandId]?.slug === slug ? { ...loadedBrandsDict[brandId], isLoading: true } : loadedBrandsDict[brandId]);
        // setOrganizationBrands(updatedBrands);

        try {
            await updateMemberStatusInOrganization(inProgressUpdatingBrandSlug?.status, selectedBrand!.id)
            toast.success("Subscription successfully updated.", SHARED_TOAST_OPTIONS);
        } catch (error) {
            console.error(error);
            toastError("There was an error enabling pro plan access for this user.");
        } finally {
            setShowUpgradeModal(false)
        }
    }

    const changeSelectedBrand = (brandId: number) => {
        setCurrentBrand(loadedBrandsDict[brandId]);
        history.push('/')
    }

    const renderOrganizationBrandsTable = () => {
        const defaultColDef: GridColDef = {
            sortable: true,
            field: 'brand',
            headerName: 'Brand',
            editable: false
        }

        // If there is a search value and no results, show a message
        if (searchValue?.length > 1 && !filteredOrganizationBrandIds.length) {
            return (
                <div className='organizationProfilesTableNoBrandsFound w-full h-16 flex justify-center items-center'>
                    <Caption color={Colors.greyDark}>No brands found for '{searchValue}'.</Caption>
                </div>
            )
        }

        const columns: GridColDef[] = [
            {
                ...defaultColDef,
                field: "brand",
                headerName: "Brand",
                flex: 2,
                renderCell: (params) => {
                    return (
                        <div className="organizationProfilesTableBrandNameCell flex items-center gap-3">
                            <CustomAvatar
                                className="organizationProfilesTablePlaceholderImage w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center"
                                image={params.row.image}
                                brandOverride={params.row}
                                isBorder={true}
                            />

                            <Body2>{params.row.name}</Body2>
                            {params.row.tier === PlanType.PRO &&
                                <UpgradeToProChip
                                    type="label"
                                    withMargin={false}
                                />
                            }
                        </div>
                    )
                },
            },
            // {
            //     ...defaultColDef,
            //     field: "fanbase",
            //     headerName: "Fanbase Size",
            //     flex: 1,
            //     renderCell: (params) => {
            //         const brand = params.row;
            //         const brandId = params.row.id
            //         return <Body2 className="organizationBrandsTableFanbaseSizeCell">
            //             {millify(loadedBrandsDict[brandId]?.fanbaseSize || getFanbaseSize(brand))}
            //         </Body2>
            //     },
            // },
            // {
            //     ...defaultColDef,
            //     field: "campaigns",
            //     headerName: "Total Campaigns",
            //     flex: 1,
            //     renderCell: (params) => {
            //         const brandId = params.row.id
            //         return (
            //             <Body2 className="organizationBrandsTableTotalCampaignsCell">
            //                 {loadedBrandsDict[brandId]?.totalCampaigns || 0} {pluralize('Campaign', loadedBrandsDict[brandId]?.totalCampaigns)}
            //             </Body2>
            //         )
            //     },
            // },
            {
                ...defaultColDef,
                field: "actions",
                headerName: "",
                flex: 3,
                renderCell: (params) => {
                    const brand = loadedBrandsDict[params.row.id] as CurrentBrand
                    const brandRow = params.row;
                    return (
                        <div className="organizationBrandsTableActionsCell ml-auto flex gap-4 items-center">
                            {brand.tier !== PlanType.PRO &&
                                <ButtonSymphony
                                    className="buttonUpgradeToProOrganizationBrands"
                                    onClick={() => beginUpgradeToPro(brand.id)}
                                    variant="outlined"
                                >
                                    Upgrade To Pro
                                </ButtonSymphony>
                            }
                            <ButtonSymphony
                                className="buttonOpenDashboardOrganizationBrands"
                                onClick={() => changeSelectedBrand(brand.id)}
                                variant="text"
                            >
                                Go To Dashboard
                            </ButtonSymphony>
                            <BrandActionMenu
                                className="brandActionMenuOrganizationTabBrands"
                                handleMenuClick={() => setSelectedBrandId(brand.id)}
                                isAdminOrOwner={isAdminOrOwner}
                                onDashboardClick={() => changeSelectedBrand(brand.id)}
                                onUpgrade={beginUpgradeToPro}
                                onDowngrade={beginDowngradeToFree}
                                canDelete={isAdminOrOwner && filteredOrganizationBrandIds.length > 1}
                                openDeleteModal={() => setIsOpenDeleteConfirmationModal(true)}
                            />
                        </div>
                    )
                }
            },
        ];

        return (
            <OrganizationViewDefaultGrid
                className="readyStateGridComponent"
                rows={filteredOrganizationBrandIds.map(brandId => loadedBrandsDict[brandId])}
                columns={columns}
                hideFooter={false}
                disableColumnMenu
                disableColumnSelector
                page={page}
                onPageChange={(newPage) => setPage(newPage)}
                itemsPerPage={PAGE_SIZE}
                rowCount={filteredOrganizationBrandIds.length}
                paginationMode="client"
                itemName="Brands"
            />
        );
    }

    /**
     * Handles the completion of adding a new brand to the organization
     * Updates the team brands list, reloads brands, and shows a success message
     */
    const onFinishAddingToOrganization = async () => {
        // await getBrands();
        // setShowAddArtistModal(false);
        toast.success("Brand successfully added.", SHARED_TOAST_OPTIONS);
    }

    const handleDelete = async () => {
        if (!selectedBrand) {
            return;
        }

        try {
            await removeBrand(selectedBrand.id);
            setIsOpenDeleteConfirmationModal(false);
            toast.success(`${selectedBrand.name} removed from team${selectedBrand.tier === PlanType.PRO ? ' and their Pro plan has been downgraded to Free.' : '.'}`);
        } catch (error) {
            toastError('There was an error deleting the brand. Please try again.');
        }
    }

    return (
        <>
            <OrganizationTabSection
                className="organizationProfilesTeamTabsSection"
                buttonClassName="organizationPageAddProfileButton"
                title="Your Brands"
                onSearch={(searchTerm: string) => setSearchValue(searchTerm)}
                buttonText="Add Brand"
                onButtonClick={() => setShowAddArtistModal(true)}
                secondaryButtonText={isSymphonyAdmin ? "Admin: Invite from Admin Search" : undefined}
                onSecondaryButtonClick={isSymphonyAdmin ? () => setOpenAdminSelector(true) : undefined}
                showDividers
                searchValue={searchValue}
            >
                {renderOrganizationBrandsTable()}

                {isSymphonyAdmin &&
                    <SearchSelector
                        mode="select"
                        onImportSelected={(brands) => adminViewImportBrands(brands)}
                        open={openAdminSelector}
                        onClose={handleCloseAdminSelectorDropdown}
                    />
                }
            </OrganizationTabSection>

            <AddBrandToOrganizationModal
                className="readyStateAddArtistModal"
                isOpen={showAddArtistModal}
                onClose={() => setShowAddArtistModal(false)}
                onFinishAddingToOrganization={onFinishAddingToOrganization}
            />
            <ConfirmChangeSubscriptionModal
                isOpen={showUpgradeModal}
                type={inProgressUpdatingBrandSlug?.status}
                onClose={() => setShowUpgradeModal(false)}
                onConfirm={handleProceedWithSubscriptionAction}
                brandName={selectedBrand?.name}
                isTeamAdmin={isAdminOrOwner}
            />
            <DeleteBrandConfirmationModal
                isOpen={isOpenDeleteConfirmationModal}
                onClose={() => setIsOpenDeleteConfirmationModal(false)}
                onDeleteAction={handleDelete}
            />
        </>
    );
}

export default OrganizationTabBrands;
